import { useState } from 'react';
import { motion } from 'framer-motion';
import { Routes, Route, Link } from 'react-router-dom';
import AboutUs from './pages/AboutUs';
import CircularText from './components/CircularText';

// Add this TypeScript declaration to fix linter error for window.confetti
declare global {
  interface Window {
    confetti?: (options: any) => void;
    turnstile?: any;
  }
}

// --- Barcode SVG ---
function Barcode({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 180 40" className={className} fill="none" aria-hidden="true">
      <rect x="0" y="0" width="180" height="40" fill="white" />
      <g stroke="black" strokeWidth="3">
        <line x1="10" y1="5" x2="10" y2="35" />
        <line x1="18" y1="5" x2="18" y2="35" />
        <line x1="26" y1="5" x2="26" y2="35" />
        <line x1="34" y1="5" x2="34" y2="35" />
        <line x1="44" y1="5" x2="44" y2="35" />
        <line x1="52" y1="5" x2="52" y2="35" />
        <line x1="60" y1="5" x2="60" y2="35" />
        <line x1="70" y1="5" x2="70" y2="35" />
        <line x1="80" y1="5" x2="80" y2="35" />
        <line x1="90" y1="5" x2="90" y2="35" />
        <line x1="100" y1="5" x2="100" y2="35" />
        <line x1="110" y1="5" x2="110" y2="35" />
        <line x1="120" y1="5" x2="120" y2="35" />
        <line x1="130" y1="5" x2="130" y2="35" />
        <line x1="140" y1="5" x2="140" y2="35" />
        <line x1="150" y1="5" x2="150" y2="35" />
        <line x1="160" y1="5" x2="160" y2="35" />
        <line x1="170" y1="5" x2="170" y2="35" />
      </g>
    </svg>
  );
}

// --- Large Stamp ---
function Stamp({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <motion.div
      className={`inline-block px-6 py-2 border-4 border-black bg-white shadow-xl font-black text-3xl md:text-6xl uppercase tracking-tighter select-none ${className}`}
      style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif', letterSpacing: '-0.06em' }}
    >
      {children}
    </motion.div>
  );
}

// --- Minimal Email Input ---
function WaitlistForm({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Confetti effect on success
  function triggerConfetti() {
    if (window.confetti) {
      window.confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }

  return (
    <form
      className="flex flex-col items-center gap-2 mt-6 w-full max-w-xs mx-auto"
      onSubmit={async e => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        setSuccess(false);
        setError(null);
        const apiUrl = import.meta.env.DEV
          ? '/api/waitlist'
          : 'https://waitlist-worker.zoomiesgp.workers.dev/waitlist';
        try {
          const res = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          const data = await res.json();
          if (res.ok && data.success) {
            setSuccess(true);
            setEmail('');
            onSuccess();
            triggerConfetti();
          } else {
            setError(data.error || 'Something went wrong');
          }
        } catch (err) {
          setError('Network error');
        }
        setLoading(false);
      }}
    >
      <input
        type="email"
        required
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="Email address"
        className="bg-white border-2 border-black text-2xl font-extrabold px-4 py-3 w-full focus:outline-none placeholder:text-black/40 rounded-lg shadow-md"
        style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}
        disabled={loading || success}
      />
      <motion.button
        type="submit"
        className="font-black text-xl px-4 py-2 bg-black text-white uppercase tracking-widest w-full mt-2 cta-glow"
        style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}
        disabled={loading || success}
        whileHover={{ scale: 1.05 }}
      >
        {loading ? '...' : 'JOIN THE WAITLIST'}
      </motion.button>
      {success && (
        <span className="block w-full text-center text-green-600 font-bold mt-2 animate-pulse">Thank you for joining! See you soon in the Zoomies & Co community!</span>
      )}
      {error && (
        <span className="block w-full text-center text-red-600 font-bold mt-2 animate-pulse">{error}</span>
      )}
      {/* Privacy note */}
      <div className="mt-2 text-xs text-black/50 text-center max-w-xs" aria-live="polite">
        We respect your privacy. No spam, ever.
      </div>
    </form>
  );
}

const perkCardVariants = {
  rest: { zIndex: 1 },
  hover: { zIndex: 2 },
};
const perkImgVariants = {
  rest: { opacity: 0, scale: 0.95 },
  hover: { opacity: 0.8, scale: 1.1 },
};

// --- Homepage Component ---
function Homepage() {
  return (
    <div className="min-h-screen w-full bg-white text-black font-sans overflow-x-hidden">
      {/* HERO BLOCK */}
      <section className="relative w-full min-h-[80vh] flex flex-col justify-center items-center px-4 py-12 border-b-4 border-black overflow-hidden">
        {/* Barcode top left */}
        <motion.div initial={{ x: -40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="absolute top-4 left-2 z-10">
          <Barcode className="w-32 h-8" />
        </motion.div>
        {/* Circular Instagram Text - bottom right, always visible on desktop */}
        <div className="fixed bottom-8 right-8 z-50">
          <a href="https://www.instagram.com/zoomiesandco.official/" target="_blank" rel="noopener noreferrer">
            <CircularText text="Follow us on Instagram • " spinDuration={18} onHover="speedUp" />
          </a>
        </div>
        {/* Giant Logo Stamp */}
        <div className="flex flex-col items-center gap-2 z-20">
          <Stamp className="mb-2">ZOOMIES & CO</Stamp>
          <span className="text-xs md:text-lg font-bold tracking-widest uppercase bg-black text-white px-2 py-1 mt-1" style={{ display: 'block' }}>AMERICA, GET READY FOR ZOOMIES</span>
        </div>
        {/* Tagline for clarity */}
        <div className="mt-2 text-center text-base md:text-xl font-semibold text-black/80 max-w-lg mx-auto z-20" style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}>
          The ultimate destination for unique, playful, and high-quality pet essentials. Sign up to get early access and exclusive perks!
        </div>
        {/* Subtitle */}
        <div className="mt-2 text-center text-lg md:text-2xl font-bold uppercase tracking-tight max-w-xl mx-auto z-20" style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}>
          Where every pet's story begins with <span className="text-pink-500">LOVE</span>
        </div>
        {/* Waitlist Form */}
        <div className="mt-6 z-20 w-full max-w-xs flex flex-col items-center">
          {/* Animated Mascot */}
          <div className="mb-2 animate-bounce flex items-center justify-center">
            <img src="/avatar.png" alt="Mascot" className="w-16 h-16 md:w-24 md:h-24 rounded-full object-cover shadow-lg border-2 border-black" />
          </div>
          <div className="w-full bg-white/90 border-2 border-black rounded-xl shadow-xl p-4 flex flex-col items-center">
            <WaitlistForm onSuccess={() => {}} />
            {/* Social proof */}
            <div className="mt-3 text-sm md:text-base text-black/60 font-medium text-center" aria-live="polite">
              Join <span className="font-bold">1,200+</span> other pet lovers on the waitlist!
            </div>
          </div>
        </div>
        {/* Shipping Perks */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6 z-20">
          <div className="px-4 py-2 bg-black text-white font-extrabold text-base md:text-lg uppercase tracking-widest border-4 border-black shadow-xl rounded rotate-[-2deg] select-none" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
            WE SHIP NATIONWIDE
          </div>
          <div className="px-4 py-2 bg-black text-white font-bold text-base md:text-lg uppercase tracking-widest border-4 border-black shadow-xl rounded rotate-2 select-none" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
            FREE SHIPPING ON ORDERS $50+
          </div>
        </div>
        {/* Overlapping barcode bottom right */}
        <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.4 }} className="absolute bottom-4 right-2 z-10">
          <Barcode className="w-32 h-8" />
        </motion.div>
        {/* Manga pet illustration placeholder */}
        <div className="absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2 z-0 opacity-10 pointer-events-none select-none">
          <div className="w-40 h-40 md:w-64 md:h-64 border-4 border-black bg-white flex items-center justify-center rounded-full">
            <span className="text-7xl md:text-9xl" aria-label="paw" role="img">🐾</span>
            {/* Placeholder: Replace with manga-style pet art */}
          </div>
        </div>
      </section>

      {/* VISUAL SECTION */}
      <section className="w-full grid grid-cols-1 md:grid-cols-2 gap-0 border-b-4 border-black min-h-[320px]">
        {/* Left: Manga pet illustration placeholder */}
        <div className="flex items-center justify-center bg-white min-h-[200px] border-b-4 md:border-b-0 md:border-r-4 border-black relative">
          <div className="w-32 h-32 md:w-56 md:h-56 border-4 border-black bg-white flex items-center justify-center rounded-full overflow-hidden">
            <img src="/paws.png" alt="Manga-style pet paws" className="object-cover w-full h-full grayscale contrast-200" />
          </div>
          <span className="absolute bottom-2 right-2 text-xs font-bold text-black/40">Manga</span>
        </div>
        {/* Right: Text block with grunge effect */}
        <div className="flex flex-col items-center justify-center p-6 md:p-12 relative overflow-visible">
          <div className="relative w-full flex flex-col items-center">
            <motion.div
              whileHover={{ x: [0, 8, -8, 0], rotate: [0, 2, -2, 0] }}
              className="text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none select-none mix-blend-multiply"
              style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif', letterSpacing: '-0.04em' }}
            >
              PAWS / FANGS / LOVE
            </motion.div>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-20 text-7xl md:text-9xl font-black select-none pointer-events-none rotate-6 whitespace-nowrap">
              PAWS / FANGS / LOVE
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS ZOOMIES & CO SECTION */}
      <section className="w-full py-16 px-4 border-b-4 border-black bg-white flex flex-col items-center">
        <div className="max-w-4xl w-full">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-4" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
              What is Zoomies & Co?
            </h2>
            <div className="w-24 h-1 bg-black mx-auto"></div>
          </div>
          
          {/* Learn About Us Button */}
          <div className="text-center">
            <Link 
              to="/about"
              className="inline-block bg-black text-white px-8 py-4 border-4 border-black hover:bg-white hover:text-black transition-all duration-300 font-bold uppercase tracking-wider shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}
            >
              Learn About Us
            </Link>
          </div>
        </div>
      </section>

      {/* TAKE A PEEK AT OUR COLLECTION SECTION */}
      <section className="w-full py-16 px-4 border-b-4 border-black bg-gray-50 flex flex-col items-center">
        <div className="max-w-4xl w-full">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-4" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
              Where the Zoomies Begin.
            </h2>
            <div className="w-24 h-1 bg-black mx-auto mb-4"></div>
            <p className="text-lg md:text-xl font-bold text-black/80" style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}>
              Only The Good Stuff
            </p>
            <p className="text-base md:text-lg text-black/60 italic" style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}>
              Tested by pets, approved by picky humans (that's us)
            </p>
          </div>
          
          {/* Show Me Button */}
          <div className="text-center">
            <button
              className="inline-block bg-black text-white px-8 py-4 border-4 border-black hover:bg-white hover:text-black transition-all duration-300 font-bold uppercase tracking-wider shadow-lg hover:shadow-xl transform hover:-translate-y-1 cursor-default"
              style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}
              disabled
            >
              Stay Tuned
            </button>
          </div>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section className="w-full py-10 px-4 border-b-4 border-black bg-white flex flex-col items-center">
        <div className="max-w-2xl w-full grid grid-cols-1 md:grid-cols-6 gap-2 md:gap-6 items-end">
          <div className="md:col-span-4 text-2xl md:text-4xl font-extrabold leading-tight uppercase" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
            The structural adaptation of <span className="font-normal">care</span> enables pets to <span className="font-normal">thrive</span>.
          </div>
          <div className="md:col-span-2 flex flex-col gap-2 mt-4 md:mt-0">
            <span className="text-base md:text-lg font-bold uppercase tracking-widest">Power to Live</span>
            <span className="text-xs md:text-sm font-normal">(The power to live)</span>
          </div>
        </div>
        <div className="mt-4 w-full text-center text-lg md:text-xl font-normal max-w-xl mx-auto">
          We believe in the biology of love, the science of play, and the poetry of paws. Zoomies & Co is for pets who are more than cute—they're wild, weird, and worthy.
        </div>
      </section>

      {/* WAITLIST PERKS */}
      <section className="w-full py-10 px-4 border-b-4 border-black bg-white flex flex-col items-center">
        <div className="flex flex-col md:flex-row gap-6 w-full max-w-3xl justify-center items-stretch">
          {/* Perk 1 */}
          <motion.div
            whileHover="hover"
            initial="rest"
            animate="rest"
            variants={perkCardVariants}
            className="flex-1 bg-black text-white border-4 border-black flex flex-col justify-center items-center px-4 py-8 min-h-[120px] relative overflow-visible"
          >
            <motion.img
              src="/pop.jpeg"
              alt="Perk pop image"
              className="absolute inset-0 w-full h-full object-cover rounded-lg z-0 pointer-events-none transition-all duration-300"
              variants={perkImgVariants}
              style={{ filter: 'blur(2px)' }}
            />
            <span className="absolute left-2 top-2 text-xs font-bold rotate-90 tracking-widest z-10">Early Access</span>
            <span className="text-2xl md:text-4xl font-black uppercase tracking-tight flex items-center gap-2 relative z-10" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
              <span role="img" aria-label="rocket" className="transition-transform group-hover:scale-125">🚀</span> Early Access
            </span>
          </motion.div>
          {/* Perk 2 */}
          <motion.div
            whileHover="hover"
            initial="rest"
            animate="rest"
            variants={perkCardVariants}
            className="flex-1 bg-black text-white border-4 border-black flex flex-col justify-center items-center px-4 py-8 min-h-[120px] relative overflow-visible"
          >
            <motion.img
              src="/pop.jpeg"
              alt="Perk pop image"
              className="absolute inset-0 w-full h-full object-cover rounded-lg z-0 pointer-events-none transition-all duration-300"
              variants={perkImgVariants}
              style={{ filter: 'blur(2px)' }}
            />
            <span className="absolute right-2 top-2 text-xs font-bold -rotate-90 tracking-widest z-10">Discount</span>
            <span className="text-2xl md:text-4xl font-black uppercase tracking-tight flex items-center gap-2 relative z-10" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
              <span role="img" aria-label="money" className="transition-transform group-hover:scale-125">💸</span> Launch Discounts
            </span>
          </motion.div>
          {/* Perk 3 */}
          <motion.div
            whileHover="hover"
            initial="rest"
            animate="rest"
            variants={perkCardVariants}
            className="flex-1 bg-black text-white border-4 border-black flex flex-col justify-center items-center px-4 py-8 min-h-[120px] relative overflow-visible"
          >
            <motion.img
              src="/pop.jpeg"
              alt="Perk pop image"
              className="absolute inset-0 w-full h-full object-cover rounded-lg z-0 pointer-events-none transition-all duration-300"
              variants={perkImgVariants}
              style={{ filter: 'blur(2px)' }}
            />
            <span className="absolute left-2 bottom-2 text-xs font-bold rotate-180 tracking-widest z-10">Reward?</span>
            <span className="text-xl md:text-2xl font-black uppercase tracking-tight text-center flex items-center gap-2 relative z-10" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
              <span role="img" aria-label="paw" className="transition-transform group-hover:scale-125">🐾</span> Your pet shall thank you<br className="hidden md:block" /> (& judge you less)
            </span>
          </motion.div>
        </div>
      </section>

      {/* Bottom Sign-Up Box */}
      <section className="w-full flex flex-col items-center justify-center py-10 px-4 bg-white border-b-4 border-black">
        <div className="w-full max-w-xs bg-white/90 border-2 border-black rounded-xl shadow-xl p-6 flex flex-col items-center">
          <div className="mb-2 text-xl md:text-2xl font-extrabold text-center">Don't miss out!<br />Sign up for early access:</div>
          <WaitlistForm onSuccess={() => {}} />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="w-full py-6 px-4 grid grid-cols-1 md:grid-cols-3 items-center bg-white border-t-4 border-black gap-4 text-xs md:text-sm">
        <div className="flex items-center gap-4 justify-center md:justify-start">
          {/* Social icons (black only, SVGs) */}
          <a href="https://www.instagram.com/zoomiesandco.official/" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform hover:opacity-70" aria-label="Instagram">
            <svg width="24" height="24" fill="none" stroke="black" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17" cy="7" r="1.5"/></svg>
          </a>
        </div>
        <div className="flex flex-col items-center justify-center text-center font-bold text-lg md:text-xl tracking-widest mx-2">
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl md:text-3xl" aria-label="paw" role="img">🐾</span>
            <span className="text-2xl md:text-3xl" aria-label="dog" role="img">🐶</span>
          </div>
          <span className="mt-1">Spreading joy, one wag at a time</span>
        </div>
        <div className="flex gap-4 justify-center md:justify-end">
          <a href="#" className="hover:underline hover:opacity-70">Contact</a>
          <a href="#" className="hover:underline hover:opacity-70">Email</a>
          <a href="#" className="hover:underline hover:opacity-70">Terms</a>
        </div>
      </footer>
    </div>
  );
}

// --- Main App Component with Routing ---
export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Homepage />} />
      <Route path="/about" element={<AboutUs />} />
    </Routes>
  );
}
